import '../styles/pdf-pages.css';
import '../styles/model-overview.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateFestivalCo2 } from '@/domain/impact-calculate-festival/entities/calculate-festival-co2.entity';
import { selectImpactCalculateFestivalCo2ForProject } from '@/domain/impact-calculate-festival/impact-calculate-festival.selectors';
import { selectCurrentProjectFestival } from '@/domain/project-festival/project-festival.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { formatProjectDatesTz } from '@/ui/utils/format-project-dates';
import { getColorScaleForScore } from '@/ui/utils/get-color-for-score';

import CalendarMonthSvg from '../assets/calendar_month.svg';
import { Co2Infography } from '../components/Co2Infography';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import { SimpleCircularProgress } from '../components/SimpleCircularProgress';
import { displayDecimal, displayTonsToKg } from '../utils/display-scores.utils';

export function PdfFestivalPage2({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const festival = useAppSelector(selectCurrentProjectFestival);
  const festivalId = festival?.id ?? 0;
  const impactCo2 = useAppSelector(
    selectImpactCalculateFestivalCo2ForProject(festivalId),
  ) satisfies CalculateFestivalCo2;

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!festival) {
    return <h1>{translate('PdfExports.festival-not-found')}</h1>;
  }

  return (
    <div id="page2" className="pdf-page">
      <div className="content">
        <div className="pdf-light-header">
          <div>
            <h1>{festival.name}</h1>
            {festival.startDate && festival.endDate && (
              <h2>
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="festival-date">
                  {formatProjectDatesTz(
                    festival.startDate || '',
                    festival.endDate || '',
                  )}
                </span>
              </h2>
            )}
          </div>
        </div>
        <div className="pdf-separator"></div>

        <div className="model-header">
          <div>
            <h2>{translate('PdfExports.co2-score-details')}</h2>
            <SimpleCircularProgress percentage={impactCo2.completion} />
          </div>
          <div>
            <h4>
              <span className="highlight">
                {displayTonsToKg(impactCo2.scoreKgeCO2)}{' '}
              </span>
              {translate('Projects.teCO2')}
            </h4>
            <h5>
              {displayDecimal(impactCo2.kgeCO2ByParticipant)}{' '}
              {translate('Projects.kgeCO2-by-participant')}
            </h5>
          </div>
          <div>
            <PdfScoreCard
              type="co2"
              scoreLetter={impactCo2.scoreLetter}
              size="medium"
            />
          </div>
        </div>

        <div className="model-content">
          <div
            style={{
              width: 350,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Projects.team')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(impactCo2.sectors.team.score)}
                  </span>{' '}
                  {translate('Projects.teCO2')}
                </div>
              </div>
              <div className="sector-value">
                <ul>
                  {Object.entries(impactCo2.sectors.team.categories).map(
                    ([key, { score }]) => (
                      <li key={key}>
                        <span>
                          {translate(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ('InviteExternalToFill.category.' + key) as any,
                          )}
                        </span>
                        <span>
                          {displayDecimal(score)} {translate('Projects.teCO2')}
                        </span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Projects.artists')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(impactCo2.sectors.artists.score)}
                  </span>{' '}
                  {translate('Projects.teCO2')}
                </div>
              </div>
              <div className="sector-value">
                <ul>
                  {Object.entries(impactCo2.sectors.artists.categories).map(
                    ([key, { score }]) => (
                      <li key={key}>
                        <span>
                          {translate(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ('InviteExternalToFill.category.' + key) as any,
                          )}
                        </span>
                        <span>
                          {displayDecimal(score)} {translate('Projects.teCO2')}
                        </span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Projects.public')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(impactCo2.sectors.public.score)}
                  </span>{' '}
                  {translate('Projects.teCO2')}
                </div>
              </div>
              <div className="sector-value">
                <ul>
                  {Object.entries(impactCo2.sectors.public.categories).map(
                    ([key, { score }]) => (
                      <li key={key}>
                        <span>
                          {translate(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ('InviteExternalToFill.category.' + key) as any,
                          )}
                        </span>
                        <span>
                          {displayDecimal(score)} {translate('Projects.teCO2')}
                        </span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Projects.site')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(impactCo2.sectors.site.score)}
                  </span>{' '}
                  {translate('Projects.teCO2')}
                </div>
              </div>
              <div className="sector-value">
                <ul>
                  {Object.entries(impactCo2.sectors.site.categories).map(
                    ([key, { score }]) => (
                      <li key={key}>
                        <span>
                          {translate(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ('InviteExternalToFill.category.' + key) as any,
                          )}
                        </span>
                        <span>
                          {displayDecimal(score)} {translate('Projects.teCO2')}
                        </span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </div>
          </div>
          <div
            style={{
              width: 350,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <Co2Infography
              items={[
                {
                  score: impactCo2.sectors.team.score,
                  label: translate('Projects.team'),
                },
                {
                  score: impactCo2.sectors.artists.score,
                  label: translate('Projects.artists'),
                },
                {
                  score: impactCo2.sectors.public.score,
                  label: translate('Projects.public'),
                },
                {
                  score: impactCo2.sectors.site.score,
                  label: translate('Projects.site'),
                },
              ]}
              colorScale={getColorScaleForScore(impactCo2.scoreLetter)}
              title={translate('PdfExports.co2-emissions-by-sector')}
            />
            <div className="model-overall-comments">
              {translate('PdfExports.comment')}
            </div>
          </div>
        </div>

        <div className="navigation-controls">
          <a href="#page1" className="tag-pill">
            {translate('PdfExports.go-back-to-overview')}
          </a>
          <a href="#page3" className="tag-pill">
            {translate('PdfExports.go-to-env')}
          </a>
        </div>
      </div>

      <PdfProjectPager current={2} total={totalPages}></PdfProjectPager>
    </div>
  );
}
